import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';
import SafeIcon from '../../common/SafeIcon';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';

const { FiWifi, FiWifiOff, FiRefreshCw, FiClock } = FiIcons;
const { 
  GiSushis, 
  GiChefToque, 
  GiFishbone, 
  GiSeaweed, 
  GiKnifeFork,
  GiCampfire,
  GiWaterDrop
} = GiIcons;

function OfflinePage() {
  const isOnline = useOnlineStatus();
  const [preparingText, setPreparingText] = useState('準備中');
  const [showReconnecting, setShowReconnecting] = useState(false);

  // 動態文字效果
  useEffect(() => {
    const interval = setInterval(() => {
      setPreparingText(prev => {
        if (prev === '準備中...') return '準備中';
        return prev + '.';
      });
    }, 600);
    return () => clearInterval(interval);
  }, []);

  // 監測重新連線
  useEffect(() => {
    if (isOnline) {
      setShowReconnecting(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  }, [isOnline]);

  // 手動重新整理
  const handleRefresh = () => {
    window.location.reload();
  };

  // 漂浮食材配置
  const floatingIngredients = [
    { icon: GiSushis, delay: 0, duration: 4, startX: 10, endX: 20 },
    { icon: GiFishbone, delay: 0.8, duration: 5, startX: 70, endX: 80 },
    { icon: GiSeaweed, delay: 1.6, duration: 4.5, startX: 30, endX: 40 },
    { icon: GiWaterDrop, delay: 2.4, duration: 3.8, startX: 85, endX: 90 },
    { icon: GiSushis, delay: 3.2, duration: 4.2, startX: 50, endX: 60 },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      
      {/* 背景漂浮食材 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingIngredients.map((item, index) => (
          <motion.div
            key={index}
            className="absolute text-4xl md:text-5xl opacity-10 dark:opacity-5"
            initial={{ 
              x: `${item.startX}vw`, 
              y: -100,
              rotate: 0 
            }}
            animate={{ 
              y: '110vh',
              rotate: 360,
              x: `${item.endX}vw`
            }}
            transition={{
              duration: item.duration,
              delay: item.delay,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            <SafeIcon icon={item.icon} />
          </motion.div>
        ))}
      </div>

      {/* 木質紋理背景 */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10" 
        style={{
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, currentColor 2px, currentColor 4px)',
          color: '#8B4513'
        }}
      />

      {/* 主內容 */}
      <motion.div
        className="text-center max-w-3xl relative z-10"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        
        {/* 重新連線提示 */}
        <AnimatePresence>
          {showReconnecting && (
            <motion.div
              className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50"
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -100, opacity: 0 }}
            >
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3">
                <SafeIcon icon={FiWifi} className="text-xl animate-pulse" />
                <span className="font-medium">網路已恢復！正在重新載入...</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 主廚圖標 + 火焰動畫 */}
        <motion.div
          className="flex justify-center mb-8 relative"
          animate={{ 
            y: [0, -12, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="relative">
            {/* 主廚帽子 */}
            <div className="text-8xl md:text-9xl text-orange-500 dark:text-orange-400">
              <SafeIcon icon={GiChefToque} />
            </div>
            
            {/* 火焰效果 */}
            <motion.div
              className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-3xl text-orange-600 dark:text-orange-500"
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <SafeIcon icon={GiCampfire} />
            </motion.div>

            {/* WiFi 離線圖標 */}
            <motion.div
              className="absolute -right-4 -top-2 text-3xl text-slate-600 dark:text-slate-400 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm rounded-full p-2"
              animate={{ 
                rotate: [0, -10, 0, 10, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <SafeIcon icon={FiWifiOff} />
            </motion.div>
          </div>
        </motion.div>

        {/* 標題區塊 */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {/* 主標題 */}
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 dark:text-white mb-4">
            廚房暫時休息中
          </h1>

          {/* 副標題 - 動態文字 */}
          <p className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 mb-3">
            主廚正在{preparingText}
          </p>

          <p className="text-base md:text-lg text-slate-500 dark:text-slate-400">
            網路連線似乎中斷了，無法取得最新的 Omakase 菜單
          </p>
        </motion.div>

        {/* 裝飾性壽司吧檯 */}
        <motion.div
          className="flex justify-center items-end gap-3 md:gap-4 mb-10"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
        >
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="text-3xl md:text-4xl"
              animate={{ 
                y: [0, -8, 0],
                rotate: [0, i % 2 === 0 ? 5 : -5, 0]
              }}
              transition={{
                duration: 2 + i * 0.2,
                repeat: Infinity,
                delay: i * 0.1,
                ease: "easeInOut"
              }}
            >
              <SafeIcon icon={GiSushis} className="text-orange-500 dark:text-orange-400" />
            </motion.div>
          ))}
        </motion.div>

        {/* 狀態卡片 */}
        <motion.div
          className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-md rounded-2xl p-6 md:p-8 mb-8 border-2 border-slate-200 dark:border-slate-700 shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <SafeIcon icon={FiClock} className="text-2xl text-orange-500 dark:text-orange-400" />
            <h2 className="text-lg md:text-xl font-semibold text-slate-800 dark:text-white">
              等待連線恢復
            </h2>
          </div>

          <p className="text-sm md:text-base text-slate-600 dark:text-slate-300 mb-6">
            就像等待完美的魚料熟成，好的體驗值得耐心等待。<br />
            請檢查您的網路連線，或稍後再試。
          </p>

          {/* 進度條動畫 */}
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden mb-6">
            <motion.div
              className="h-full bg-gradient-to-r from-orange-500 via-rose-500 to-orange-500 rounded-full"
              animate={{ 
                x: ['-100%', '100%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{ width: '50%' }}
            />
          </div>

          {/* 重新整理按鈕 */}
          <motion.button
            onClick={handleRefresh}
            className="group w-full md:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-xl hover:from-orange-600 hover:to-rose-600 transition-all duration-300 shadow-lg hover:shadow-xl font-medium flex items-center justify-center gap-3"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <SafeIcon icon={FiRefreshCw} className="text-xl group-hover:rotate-180 transition-transform duration-500" />
            <span>重新嘗試連線</span>
          </motion.button>
        </motion.div>

        {/* 裝飾性筷子 */}
        <motion.div
          className="flex justify-center gap-2 mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <motion.div
            className="w-1 h-16 md:h-20 bg-gradient-to-b from-amber-600 to-amber-800 dark:from-amber-500 dark:to-amber-700 rounded-full"
            animate={{ rotate: [0, -3, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.div
            className="w-1 h-16 md:h-20 bg-gradient-to-b from-amber-600 to-amber-800 dark:from-amber-500 dark:to-amber-700 rounded-full"
            animate={{ rotate: [0, 3, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          />
        </motion.div>

        {/* 提示資訊 */}
        <motion.div
          className="space-y-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <SafeIcon icon={GiKnifeFork} className="text-lg" />
            <span>某些已快取的頁面可能仍可瀏覽</span>
          </div>

          <p className="text-xs text-slate-400 dark:text-slate-500 italic">
            "最好的料理，來自完美的時機" - 板前職人
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default OfflinePage;